import { useState } from 'react';
import { AlertTriangle, X, Shield } from 'lucide-react';

/**
 * Extended Session Disclaimer Modal
 * 
 * Shows liability disclaimer when user opts for 30-day session.
 * Requires explicit checkbox acceptance before proceeding.
 */
const ExtendedSessionDisclaimerModal = ({ isOpen, onAccept, onCancel }) => {
  const [accepted, setAccepted] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (accepted) {
      onAccept(new Date().toISOString());
    }
  };

  // Prevent closing by clicking outside or pressing Escape
  const handleBackdropClick = (e) => {
    // Do nothing - modal must be explicitly closed
    e.stopPropagation();
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e1e1e',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid #333'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #333'
        }}>
          <div style={{
            backgroundColor: '#f59e0b20',
            borderRadius: '50%',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AlertTriangle size={28} color="#f59e0b" />
          </div>
          <div>
            <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px' }}>
              Extended Login Session
            </h2>
            <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
              Please read carefully before proceeding
            </p>
          </div>
        </div>

        {/* Disclaimer Content */}
        <div style={{ 
          backgroundColor: '#2a2a2a', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '20px',
          border: '1px solid #444'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '12px',
            marginBottom: '12px'
          }}>
            <Shield size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ 
              color: 'var(--text-secondary)', 
              margin: 0, 
              fontSize: '14px', 
              lineHeight: '1.6' 
            }}>
              By enabling extended login, you acknowledge that your session will remain 
              active for <strong style={{ color: 'var(--text-primary)' }}>30 days</strong>.
            </p>
          </div>
          
          <p style={{ 
            color: 'var(--text-secondary)', 
            margin: '12px 0 0 32px', 
            fontSize: '14px', 
            lineHeight: '1.6' 
          }}>
            If your device is lost, stolen, or accessed by an unauthorized person, 
            they may gain access to security systems including:
          </p>
          
          <ul style={{ 
            color: '#f87171', 
            margin: '12px 0 0 48px', 
            fontSize: '14px',
            lineHeight: '1.8'
          }}>
            <li>Live camera feeds and recordings</li>
            <li>Door access controls</li>
            <li>Emergency lockdown features</li>
            <li>Intercom systems</li>
          </ul>
        </div>

        {/* Liability Notice */}
        <div style={{
          backgroundColor: '#7f1d1d20',
          border: '1px solid #7f1d1d',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px'
        }}>
          <p style={{ 
            color: '#fca5a5', 
            margin: 0, 
            fontSize: '13px', 
            lineHeight: '1.5' 
          }}>
            <strong>Liability Notice:</strong> Serverli Inc. is not liable for any damages, 
            security breaches, or unauthorized access resulting from extended sessions. 
            You can revoke this session at any time from your account settings.
          </p>
        </div>

        {/* Checkbox */}
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          cursor: 'pointer',
          padding: '12px',
          backgroundColor: accepted ? '#10b98120' : 'transparent',
          borderRadius: '8px',
          border: `1px solid ${accepted ? '#10b981' : '#444'}`,
          marginBottom: '20px',
          transition: 'all 0.2s'
        }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            style={{
              width: '20px',
              height: '20px',
              marginTop: '2px',
              accentColor: '#10b981',
              cursor: 'pointer'
            }}
          />
          <span style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: '1.5' }}>
            I understand the security risks and accept responsibility for keeping my device secure
          </span>
        </label>

        {/* Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end' 
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #444',
              backgroundColor: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#333'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            Cancel
          </button>
          <button
            onClick={handleAccept}
            disabled={!accepted}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: accepted ? '#10b981' : '#333',
              color: accepted ? '#fff' : '#666',
              cursor: accepted ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            Enable Extended Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExtendedSessionDisclaimerModal;
